import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Search, Filter, LogOut, Download, Mail, Briefcase,
  ArrowUpDown, ArrowUp, ArrowDown, X, UserCheck, Eye, Settings,
  EyeOff, ChevronRight, FolderPlus, Folder, UserPlus, UserMinus,
  FileText, GraduationCap, Award, ArrowLeft, Bell, MessageSquare,
  Database, ChevronDown, CheckCircle, Send, Globe, Lock, User,
  Building2, Edit, XCircle, Trash2, Calendar, Clock, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import LanguageSelector from '../components/LanguageSelector';
import api from '../services/api';
import CandidateProfileView from '../components/CandidateProfileView';
import NotificationBell from '../components/NotificationBell';
import { calculateProfileCompletion } from '../utils/profileCompletion';

import SidebarItem from '../components/SidebarItem';
import ProfileEditor from '../components/ProfileEditor';
import TeamMembersTab from '../components/TeamMembersTab';
import PersonalProfileTab from '../components/PersonalProfileTab';
import JobsTab from '../components/JobsTab';
import RecruitingOverviewTab from '../components/RecruitingOverviewTab';

const RecruitingPartnerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { showToast } = useToast();

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/talent-pool')) return 'talent-pool';
    if (path.includes('/my-requests')) return 'my-requests';
    if (path.includes('/my-profile')) return 'my-profile';
    if (path.includes('/team-members')) return 'team-members';
    if (path.includes('/jobs')) return 'jobs';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/my-feedbacks')) return 'my-feedbacks';
    if (path.includes('/all-candidates')) return 'all-candidates';
    if (path.includes('/interviews')) return 'interviews';
    return 'overview';
  };
  const activeTab = getActiveTab();

  const [candidates, setCandidates] = useState([]);
  const [totalApprovedCandidates, setTotalApprovedCandidates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobCandidates, setJobCandidates] = useState([]);
  const [jobRejected, setJobRejected] = useState([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  // fetchIdRef tracks the most recent fetchCandidates invocation.
  // If the user navigates away before a response arrives, the old callback
  // sees a stale id and skips setState — no HTTP/2 RST_STREAM sent to server.
  const fetchIdRef = useRef(0);
  const [candidateSubTab, setCandidateSubTab] = useState('new'); // 'new', 'under-review', 'rejected'
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    germanLevel: 'all',
    profileStatus: 'all',
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'name',
    direction: 'asc'
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [viewingCandidateProfile, setViewingCandidateProfile] = useState(false); // Show profile as page view
  const [interestedCandidates, setInterestedCandidates] = useState(new Set());
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false); // Header filter dropdown
  const [showSortDropdown, setShowSortDropdown] = useState(false); // Header sort dropdown
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [manualPools, setManualPools] = useState([]);
  const [showCreatePoolModal, setShowCreatePoolModal] = useState(false);
  const [newPoolName, setNewPoolName] = useState('');
  const [newPoolIsPublic, setNewPoolIsPublic] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolCandidates, setPoolCandidates] = useState([]);
  const [poolCandidatesLoading, setPoolCandidatesLoading] = useState(false);
  const [viewingCandidateInPool, setViewingCandidateInPool] = useState(false); // Track if viewing candidate profile in pool modal
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [addCandidateModalLoading, setAddCandidateModalLoading] = useState(false);
  const [showInterestConfirmationModal, setShowInterestConfirmationModal] = useState(false);
  const [interestedPools, setInterestedPools] = useState(new Set());

  // Recruiter requests state
  const [recruiterRequests, setRecruiterRequests] = useState([]);
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewingRequestDetails, setViewingRequestDetails] = useState(false); // Show request details as page view
  const [showRequestCandidatesModal, setShowRequestCandidatesModal] = useState(false);
  const [showAddToPoolModal, setShowAddToPoolModal] = useState(false);
  const [candidatesToAddToPool, setCandidatesToAddToPool] = useState([]);
  const [showAddSingleCandidateToPoolModal, setShowAddSingleCandidateToPoolModal] = useState(false);
  const [candidateToAddToPool, setCandidateToAddToPool] = useState(null);
  const [newRequestRequirements, setNewRequestRequirements] = useState('');
  const [newRequestTitle, setNewRequestTitle] = useState('');

  // My Feedbacks state
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackResponses, setFeedbackResponses] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [isSendingResponse, setIsSendingResponse] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);

  const [fullUser, setFullUser] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Helper function to view documents securely
  const handleViewDocument = (dataUri) => {
    if (!dataUri) return;

    // For legacy URLs or non-base64, use getPhotoUrl (if it's a relative path) and standard window.open
    if (!dataUri.startsWith('data:')) {
      const url = (dataUri.startsWith('http://') || dataUri.startsWith('https://'))
        ? dataUri
        : getPhotoUrl(dataUri);
      window.open(url, '_blank');
      return;
    }

    try {
      // Convert data URI to Blob
      const parts = dataUri.split(';base64,');
      if (parts.length !== 2) {
        window.open(dataUri, '_blank');
        return;
      }

      const contentType = parts[0].split(':')[1];
      const byteCharacters = atob(parts[1]);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);

      window.open(blobUrl, '_blank');
    } catch (e) {
      console.error('Error viewing document:', e);
      window.open(dataUri, '_blank');
    }
  };

  // Helper function to get the correct photo URL (matches StaffDashboard implementation)
  const getPhotoUrl = (photoUrl, updated_at = null) => {
    if (!photoUrl) {
      return null;
    }

    // If it's a data URI (base64), return as is
    if (photoUrl.startsWith('data:')) {
      return photoUrl;
    }

    // Get base URL - use environment variable or construct from current origin
    let baseUrl = process.env.REACT_APP_API_URL;

    // If no env var, construct from current window location
    if (!baseUrl) {
      const origin = window.location.origin;
      // Replace port 3000 with 8080 for backend
      baseUrl = origin.replace(':3000', ':8080');
    }

    // Remove /api from the end or middle of the URL
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    } else if (baseUrl.includes('/api/')) {
      baseUrl = baseUrl.replace('/api/', '/');
    } else if (baseUrl.endsWith('/api/')) {
      baseUrl = baseUrl.slice(0, -5);
    }

    baseUrl = baseUrl.replace(/\/$/, '');
    const normalizedPhotoUrl = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;

    let fullUrl = `${baseUrl}${normalizedPhotoUrl}`;
    if (updated_at) {
      const timestamp = new Date(updated_at).getTime();
      fullUrl += `?t=${timestamp}`;
    }
    return fullUrl;
  };
  const fetchFullUser = async () => {
    try {
      const response = await api.get('/recruiting/profile');
      setFullUser(response.data);
    } catch (error) {
      console.error('Error fetching full user profile:', error);
    }
  };

  // Fetch user profile once on mount so the header logo is available
  useEffect(() => { fetchFullUser(); }, []);

  // Fetch data based on active tab — depends on [activeTab] NOT [location.pathname].
  // This matches the StaffDashboard pattern and prevents multiple re-fires when
  // the redirect useEffect changes pathname without changing the logical tab.
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        if (activeTab === 'all-candidates') {
          fetchCandidates();
          fetchRecruiterRequests();
          fetchManualPools();
        } else if (activeTab === 'talent-pool') {
          fetchManualPools();
          fetchRecruiterRequests();
        } else if (activeTab === 'my-requests') {
          fetchRecruiterRequests();
          fetchMyFeedbackRequests();
        } else if (activeTab === 'my-feedbacks') {
          fetchMyFeedbacks();
        } else if (activeTab === 'profile') {
          fetchFullUser();
        } else if (activeTab === 'jobs') {
          fetchJobs();
        } else if (activeTab === 'interviews') {
          fetchInterviews();
        }
      } catch (error) {
        console.error('[RecruitingDashboard] Error in useEffect:', error);
      }
    }, 0);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Redirect to dashboard if on base recruiting path
  useEffect(() => {
    if (location.pathname === '/recruiting' || location.pathname === '/recruiting/') {
      navigate('/recruiting/all-candidates', { replace: true });
    }
  }, [location.pathname, navigate]);

  const fetchCandidates = async () => {
    // Increment the fetch counter; capture this invocation's id.
    // If the user navigates away before this response arrives, fetchIdRef.current
    // will have been incremented again so we skip stale setState calls.
    // This avoids sending HTTP/2 RST_STREAM via AbortController which was
    // corrupting the shared Kong connection and causing ERR_HTTP2_PROTOCOL_ERROR.
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const response = await api.get('/recruiting/candidates');
      // Drop stale response if user navigated away
      if (fetchId !== fetchIdRef.current) return;
      console.log('[PartnerDashboard] Fetched candidates response:', response.data);
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
        setCandidates(response.data);
      } else if (response.data?.candidates) {
        candidatesList = response.data.candidates;
        setCandidates(response.data.candidates);
      } else {
        candidatesList = [];
        setCandidates([]);
      }
      setTotalApprovedCandidates(candidatesList.length);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return; // stale – don't show error
      console.error('Error fetching candidates:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to fetch candidates';
      console.error('Error details:', errorMessage);
      setCandidates([]);
      setTotalApprovedCandidates(0);
      if (error.response?.status !== 404) {
        showToast(`Error fetching candidates: ${errorMessage}`);
      }
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  };

  const fetchManualPools = async () => {
    try {
      const response = await api.get('/recruiting/pools');
      setManualPools(response.data.pools || []);
    } catch (error) {
      console.error('Error fetching manual pools:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/recruiting/jobs');
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobCandidates = async (jobId) => {
    try {
      const response = await api.get(`/recruiting/jobs/${jobId}/candidates`);
      setJobCandidates(response.data.candidates || []);
      setJobRejected(response.data.rejected || []);
    } catch (error) {
      console.error('Error fetching job candidates:', error);
    }
  };

  const fetchRecruiterRequests = async () => {
    try {
      const response = await api.get('/recruiting/requests');
      setRecruiterRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching recruiter requests:', error);
    }
  };

  const fetchMyFeedbacks = async () => {
    try {
      const response = await api.get('/recruiting/my-feedbacks?feedback_type=feedback');
      setMyFeedbacks(response.data || []);
    } catch (error) {
      console.error('Error fetching my feedbacks:', error);
      setMyFeedbacks([]);
    }
  };

  const fetchMyFeedbackRequests = async () => {
    try {
      const response = await api.get('/recruiting/my-feedbacks?feedback_type=request');
      // Merge with existing recruiter_requests
      const feedbackRequests = response.data || [];
      // Get current recruiter_requests
      const currentRequests = recruiterRequests.filter(r => !r.from_feedback);
      // Combine both types of requests
      const allRequests = [...currentRequests, ...feedbackRequests.map(fr => ({
        id: fr.id,
        requirements: fr.message,
        status: fr.status === 'responded' ? 'approved' : (fr.status === 'resolved' ? 'approved' : 'pending'),
        created_at: fr.created_at,
        updated_at: fr.updated_at,
        from_feedback: true,
        feedback_id: fr.id,
        response_count: fr.response_count
      }))];
      // Sort by created_at descending
      allRequests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRecruiterRequests(allRequests);
    } catch (error) {
      console.error('Error fetching feedback requests:', error);
    }
  };

  const fetchInterviews = async () => {
    try {
      setInterviewsLoading(true);
      const response = await api.get('/recruiting/interviews');
      setInterviews(response.data || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      setInterviews([]);
    } finally {
      setInterviewsLoading(false);
    }
  };

  const fetchFeedbackResponses = async (feedbackId) => {
    try {
      const response = await api.get(`/recruiting/my-feedbacks/${feedbackId}/responses`);
      setFeedbackResponses(response.data || []);
    } catch (error) {
      console.error('Error fetching feedback responses:', error);
      setFeedbackResponses([]);
    }
  };

  const handleViewFeedback = async (feedback) => {
    setSelectedFeedback(feedback);
    setShowFeedbackModal(true);
    await fetchFeedbackResponses(feedback.id);
  };

  const handleSendResponse = async () => {
    if (!responseText.trim() || !selectedFeedback) return;
    setIsSendingResponse(true);
    try {
      await api.post(`/recruiting/my-feedbacks/${selectedFeedback.id}/respond`, {
        response_text: responseText
      });
      setResponseText('');
      fetchFeedbackResponses(selectedFeedback.id);
      fetchMyFeedbacks(); // To update response count if visible
    } catch (error) {
      console.error('Error sending response:', error);
      showToast('Failed to send response');
    } finally {
      setIsSendingResponse(false);
    }
  };

  const handleMarkFeedbackResolved = async () => {
    if (!selectedFeedback || !window.confirm('Are you sure you want to mark this as resolved?')) return;
    try {
      await api.put(`/recruiting/my-feedbacks/${selectedFeedback.id}/status`, {
        status: 'resolved'
      });
      setSelectedFeedback({ ...selectedFeedback, status: 'resolved' });
      fetchMyFeedbacks();
    } catch (error) {
      console.error('Error resolving feedback:', error);
      showToast('Failed to resolve feedback');
    }
  };


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleViewDetails = (candidate, fromPool = false) => {
    // Set candidate immediately — CandidateProfileView will fetch full details internally
    setSelectedCandidate(candidate);

    if (fromPool) {
      setViewingCandidateInPool(true);
    } else {
      setViewingCandidateProfile(true);
    }
  };

  const handleBackToList = () => {
    setViewingCandidateProfile(false);
    setSelectedCandidate(null);
  };

  const handleShowInterest = async (candidateId) => {
    try {
      await api.post(`/recruiting/candidates/${candidateId}/interest`);
      setShowInterestConfirmationModal(true);
      // Don't disable the button - allow sending interest multiple times
    } catch (error) {
      console.error('Error showing interest:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to send interest notification';
      showToast(`Failed to send interest: ${errorMessage}`);
    }
  };

  const handleShowPoolInterest = async (poolId) => {
    try {
      const response = await api.post(`/recruiting/pools/${poolId}/interest`);
      setShowInterestConfirmationModal(true);
      // Don't disable the button - allow sending interest multiple times
    } catch (error) {
      console.error('Error showing pool interest:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to send pool interest notification';
      showToast(`Failed to send pool interest: ${errorMessage}`);
    }
  };

  useEffect(() => {
    const handleInterestEvent = (e) => {
      if (e.detail?.candidateId) {
        handleShowInterest(e.detail.candidateId);
      }
    };
    window.addEventListener('sendInterest', handleInterestEvent);
    return () => window.removeEventListener('sendInterest', handleInterestEvent);
  }, [handleShowInterest]);

  // Removed handleDownloadResume - recruiters cannot download resumes directly
  // They can only send interest to staff/admin

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getProfileCompletion = (candidate) => {
    return calculateProfileCompletion(candidate).score;
  };

  const handleCreatePool = async () => {
    if (!newPoolName.trim()) {
      showToast('Please enter a pool name');
      return;
    }
    try {
      const response = await api.post('/recruiting/pools', {
        name: newPoolName,
        is_public: newPoolIsPublic
      });
      // Handle both response formats
      const newPool = response.data?.pool || response.data;
      if (newPool) {
        setManualPools([...manualPools, newPool]);
      }
      setNewPoolName('');
      setNewPoolIsPublic(false);
      setShowCreatePoolModal(false);
      // Refresh pools list
      await fetchManualPools();
    } catch (error) {
      console.error('Error creating pool:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create pool';
      showToast(`Failed to create pool: ${errorMessage}`);
    }
  };

  const handleTogglePoolVisibility = async (pool, currentVisibility) => {
    try {
      await api.put(`/recruiting/pools/${pool.id}`, {
        name: pool.name,
        is_public: !currentVisibility
      });
      fetchManualPools();
    } catch (error) {
      showToast('Failed to update pool visibility');
    }
  };

  const handleDeletePool = async (poolId) => {
    if (!window.confirm('Are you sure you want to delete this pool? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/recruiting/pools/${poolId}`);
      showToast('Pool deleted successfully');
      setSelectedPool(null);
      fetchManualPools();
    } catch (error) {
      console.error('Error deleting pool:', error);
      showToast(error.response?.data?.error || 'Failed to delete pool');
    }
  };

  const handleManagePool = async (pool) => {
    setSelectedPool(pool);
    setPoolCandidatesLoading(true);
    try {
      const response = await api.get(`/recruiting/pools/${pool.id}/candidates`);
      // Handle both array and object responses
      let candidatesList = [];
      if (Array.isArray(response.data)) {
        candidatesList = response.data;
      } else if (response.data?.candidates) {
        candidatesList = response.data.candidates;
      }
      setPoolCandidates(candidatesList);
    } catch (error) {
      console.error('Error fetching pool candidates:', error);
      setPoolCandidates([]);
    } finally {
      setPoolCandidatesLoading(false);
    }
  };

  const handleAddToPool = async (poolId, candidateId) => {
    try {
      const response = await api.post(`/recruiting/pools/${poolId}/candidates`, { candidate_ids: [candidateId] });
      // Refresh pool candidates
      await handleManagePool({ id: poolId });
      showToast('Candidate added to pool successfully');
    } catch (error) {
      console.error('Error adding candidate to pool:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to add candidate to pool';
      showToast(`Failed to add candidate to pool: ${errorMessage}`);
    }
  };

  const handleRemoveFromPool = async (poolId, candidateId) => {
    try {
      await api.delete(`/recruiting/pools/${poolId}/candidates/${candidateId}`);
      await handleManagePool({ id: poolId });
      showToast('Candidate removed from pool');
    } catch (error) {
      console.error('Error removing candidate from pool:', error);
      showToast('Failed to remove candidate from pool');
    }
  };

  const handleViewRequestCandidates = (request) => {
    setSelectedRequest(request);
    setViewingRequestDetails(true); // Show as page view instead of modal
  };

  const handleBackToRequests = () => {
    setViewingRequestDetails(false);
    setSelectedRequest(null);
  };

  const handleAddCandidatesToPool = (candidateIds) => {
    setCandidatesToAddToPool(candidateIds);
    setShowAddToPoolModal(true);
  };

  const handleAddSelectedCandidatesToPool = async (poolId) => {
    try {
      await api.post(`/recruiting/pools/${poolId}/candidates`, {
        candidate_ids: candidatesToAddToPool
      });
      showToast(`Successfully added ${candidatesToAddToPool.length} candidate(s) to pool`);
      setShowAddToPoolModal(false);
      setCandidatesToAddToPool([]);
      // Refresh requests to update granted candidates
      await fetchRecruiterRequests();
      // Refresh pools
      await fetchManualPools();
    } catch (error) {
      console.error('Error adding candidates to pool:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to add candidates to pool';
      showToast(`Failed to add candidates to pool: ${errorMessage}`);
    }
  };

  const handleCreatePoolFromRequest = async (poolName) => {
    try {
      // Create new pool
      const poolResponse = await api.post('/recruiting/pools', { name: poolName });
      const newPoolId = poolResponse.data.pool?.id || poolResponse.data.id;

      // Add all candidates from request to the new pool
      if (candidatesToAddToPool.length > 0) {
        await api.post(`/recruiting/pools/${newPoolId}/candidates`, {
          candidate_ids: candidatesToAddToPool
        });
      }

      showToast(`Successfully created pool "${poolName}" with ${candidatesToAddToPool.length} candidate(s)`);
      setShowAddToPoolModal(false);
      setCandidatesToAddToPool([]);
      setShowRequestCandidatesModal(false);
      // Refresh pools and requests
      await fetchManualPools();
      await fetchRecruiterRequests();
    } catch (error) {
      console.error('Error creating pool from request:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create pool';
      showToast(`Failed to create pool: ${errorMessage}`);
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequestTitle.trim()) {
      showToast('Please enter a title for the request');
      return;
    }
    if (!newRequestRequirements.trim()) {
      showToast('Please enter requirements');
      return;
    }
    try {
      await api.post('/recruiting/requests', {
        title: newRequestTitle.trim(),
        requirements: newRequestRequirements.trim()
      });
      setNewRequestTitle('');
      setNewRequestRequirements('');
      setShowCreateRequestModal(false);
      await fetchRecruiterRequests();
      showToast('Request created successfully');
    } catch (error) {
      console.error('Error creating request:', error);
      showToast('Failed to create request');
    }
  };

  const handleManageCandidates = (request) => {
    // Navigate to Staff Dashboard Recruiter Visibility tab with pre-selected recruiter
    navigate('/staff/dashboard?tab=recruiter-visibility&recruiterId=' + request.recruiter_id);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      await api.post('/auth/reset-password', {
        password: newPassword,
        token: localStorage.getItem('token')
      });
      showToast('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to reset password');
    }
  };

  // Filter candidates
  const filteredCandidates = (Array.isArray(candidates) ? candidates : []).filter(candidate => {
    if (!candidate) return false;
    const matchesSearch =
      candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (candidate.qualification && candidate.qualification.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (candidate.experience && candidate.experience.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesGermanLevel = filters.germanLevel === 'all' || candidate.german_level === filters.germanLevel;
    const matchesProfileStatus = filters.profileStatus === 'all' || candidate.profile_status === filters.profileStatus;

    return matchesSearch && matchesGermanLevel && matchesProfileStatus;
  });

  // Sort candidates
  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    const aVal = a[sortConfig.field] || '';
    const bVal = b[sortConfig.field] || '';

    if (sortConfig.field === 'created_at') {
      const aDate = new Date(aVal);
      const bDate = new Date(bVal);
      return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
    }

    if (sortConfig.direction === 'asc') {
      return aVal.toString().localeCompare(bVal.toString());
    } else {
      return bVal.toString().localeCompare(aVal.toString());
    }
  });

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="h-4 w-4 text-cyan-600" /> :
      <ArrowDown className="h-4 w-4 text-cyan-600" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-slate-900 text-sm">
      {/* Left Sidebar - Slightly Larger */}
      <div className="w-60 bg-white border-r border-slate-200 shadow-xl z-30 flex flex-col transition-all duration-300">
        <div className="p-4 border-b border-slate-100 bg-white/50 backdrop-blur-sm flex items-center gap-2.5">
          <div className="relative">
            <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-1 shadow-lg shadow-cyan-500/10 border border-slate-200">
              <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-emerald-500 rounded-full border border-white"></div>
          </div>
          <div>
            <h1 className="text-sm font-bold text-cyan-700 tracking-tight">Polyglots</h1>
            <p className="text-[11px] text-slate-500 font-medium whitespace-nowrap">{t.recruitingDashboard || 'Employer Portal'}</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 px-2">Main Menu</p>
          <SidebarItem
            theme="light"
            icon={Globe}
            label={t('Overview') || "Overview"}
            active={activeTab === 'overview'}
            onClick={() => navigate('/recruiting/overview')}
          />
          <SidebarItem
            theme="light"
            icon={Users}
            label={t.candidates || "All Candidates"}
            active={activeTab === 'all-candidates'}
            onClick={() => navigate('/recruiting/all-candidates')}
          />
          <SidebarItem
            theme="light"
            icon={Database}
            label={t('dashboard.talentPools')}
            active={activeTab === 'talent-pool'}
            onClick={() => navigate('/recruiting/talent-pool')}
          />
          <SidebarItem
            theme="light"
            icon={Briefcase}
            label={t.myRequests || "My Requests"}
            active={activeTab === 'my-requests'}
            onClick={() => navigate('/recruiting/my-requests')}
            badge={recruiterRequests.length > 0 ? recruiterRequests.length : null}
          />
          <SidebarItem
            theme="light"
            icon={Briefcase}
            label="Jobs"
            active={activeTab === 'jobs'}
            onClick={() => navigate('/recruiting/jobs')}
            badge={jobs.length > 0 ? jobs.length : null}
          />
          <SidebarItem
            theme="light"
            icon={Calendar}
            label="Interviews"
            active={activeTab === 'interviews'}
            onClick={() => navigate('/recruiting/interviews')}
            badge={interviews.length > 0 ? interviews.length : null}
          />

          <SidebarItem
            theme="light"
            icon={Building2}
            label={t.companyProfile || "Company Profile"}
            active={activeTab === 'profile'}
            onClick={() => navigate('/recruiting/profile')}
          />
          <SidebarItem
            theme="light"
            icon={User}
            label={t.myProfile || "My Profile"}
            active={activeTab === 'my-profile'}
            onClick={() => navigate('/recruiting/my-profile')}
          />
          <SidebarItem
            theme="light"
            icon={Users}
            label={t.teamMembers || "Team Members"}
            active={activeTab === 'team-members'}
            onClick={() => navigate('/recruiting/team-members')}
          />
        </nav>

        <div className="p-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all group text-xs"
          >
            <LogOut className="h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50 relative">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-cyan-50/50 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-50/30 rounded-full blur-3xl pointer-events-none translate-x-1/2 -translate-y-1/2" />

        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-40 shadow-sm">
          <div className="w-full px-5 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Search + Filter/Sort Group */}
              <div className="flex items-center gap-2 flex-1">
                {/* Search */}
                <div className="relative max-w-sm group hidden md:block flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-slate-400 group-focus-within:text-cyan-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder={t.searchCandidates || "Search candidates..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all shadow-sm text-sm"
                  />
                </div>

                {/* Filter & Sort Dropdowns - Only on All Candidates and Talent Pools tabs */}
                {(activeTab === 'all-candidates' || activeTab === 'talent-pool') && (
                  <div className="flex items-center gap-2">
                    {/* Filter Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => { setShowFilterDropdown(!showFilterDropdown); setShowSortDropdown(false); }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${filters.germanLevel !== 'all' || filters.profileStatus !== 'all'
                          ? 'bg-cyan-50 border-cyan-200 text-cyan-700'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                      >
                        <Filter className="h-4 w-4" />
                        <span>Filter</span>
                        {(filters.germanLevel !== 'all' || filters.profileStatus !== 'all') && (
                          <span className="bg-cyan-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                            {[filters.germanLevel !== 'all', filters.profileStatus !== 'all'].filter(Boolean).length}
                          </span>
                        )}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      {showFilterDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowFilterDropdown(false)} />
                          <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50 animate-scaleIn origin-top-left">
                            <div className="px-4 pb-2 mb-2 border-b border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Filters</p>
                            </div>
                            <div className="px-4 space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">German Level</label>
                                <select
                                  value={filters.germanLevel}
                                  onChange={(e) => setFilters({ ...filters, germanLevel: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                                >
                                  <option value="all">All Levels</option>
                                  <option value="A1">A1</option>
                                  <option value="A2">A2</option>
                                  <option value="B1">B1</option>
                                  <option value="B2">B2</option>
                                  <option value="C1">C1</option>
                                  <option value="C2">C2</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Profile Status</label>
                                <select
                                  value={filters.profileStatus}
                                  onChange={(e) => setFilters({ ...filters, profileStatus: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                                >
                                  <option value="all">All Profiles</option>
                                  <option value="complete">Complete</option>
                                  <option value="incomplete">Incomplete</option>
                                </select>
                              </div>
                              <button
                                onClick={() => { setFilters({ germanLevel: 'all', profileStatus: 'all' }); setShowFilterDropdown(false); }}
                                className="w-full text-sm text-cyan-600 hover:text-cyan-700 font-medium py-2"
                              >
                                Clear All Filters
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Sort Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => { setShowSortDropdown(!showSortDropdown); setShowFilterDropdown(false); }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium transition-all"
                      >
                        <ArrowUpDown className="h-4 w-4" />
                        <span>Sort</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      {showSortDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowSortDropdown(false)} />
                          <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-3 z-50 animate-scaleIn origin-top-left">
                            <div className="px-4 pb-2 mb-2 border-b border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Sort By</p>
                            </div>
                            <div className="px-2 space-y-1">
                              <button
                                onClick={() => { setSortConfig({ field: 'name', direction: sortConfig.field === 'name' && sortConfig.direction === 'asc' ? 'desc' : 'asc' }); setShowSortDropdown(false); }}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg flex items-center justify-between transition-colors ${sortConfig.field === 'name' ? 'bg-cyan-50 text-cyan-700' : 'text-gray-600 hover:bg-gray-50'}`}
                              >
                                <span>Name</span>
                                {sortConfig.field === 'name' && (sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                              </button>
                              <button
                                onClick={() => { setSortConfig({ field: 'german_level', direction: sortConfig.field === 'german_level' && sortConfig.direction === 'asc' ? 'desc' : 'asc' }); setShowSortDropdown(false); }}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg flex items-center justify-between transition-colors ${sortConfig.field === 'german_level' ? 'bg-cyan-50 text-cyan-700' : 'text-gray-600 hover:bg-gray-50'}`}
                              >
                                <span>German Level</span>
                                {sortConfig.field === 'german_level' && (sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <NotificationBell userRole={user?.role || 'recruiting_partner'} />

                <div className="h-6 w-px bg-gray-200"></div>

                <LanguageSelector />

                <div className="relative">
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200"
                  >
                    <div className="h-8 w-8 rounded-md flex items-center justify-center shadow-sm text-sm overflow-hidden">
                      {(fullUser?.logo_url || user?.logo_url) ? (
                        <img src={getPhotoUrl(fullUser?.logo_url || user?.logo_url)} alt={user?.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-cyan-600 to-cyan-600 flex items-center justify-center text-white font-bold">
                          {user?.name?.charAt(0)?.toUpperCase() || 'P'}
                        </div>
                      )}
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-bold text-slate-800 leading-tight">{user?.name || 'Partner'}</p>
                      <p className="text-[11px] text-slate-500">{user?.role?.replace('_', ' ') || 'Partner'}</p>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 ring-1 ring-black ring-opacity-5 py-2 z-50 animate-scaleIn origin-top-right">
                      <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Signed in as</p>
                        <p className="text-sm font-bold text-gray-900 truncate">{user?.email}</p>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            setShowPasswordModal(true);
                            setShowProfileDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2.5 text-sm text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-xl flex items-center space-x-3 transition-colors font-medium"
                        >
                          <Settings className="h-4 w-4" />
                          <span>Account Settings</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-xl flex items-center space-x-3 transition-colors font-medium"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50 custom-scrollbar relative z-10">

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <RecruitingOverviewTab
              user={fullUser || user}
              onNavigateToTab={(tab) => navigate(`/recruiting/${tab}`)}
              onSelectJob={(jobId) => {
                const job = jobs.find(j => j.id === jobId);
                if (job) {
                  setSelectedJob(job);
                  navigate('/recruiting/jobs');
                } else {
                  navigate('/recruiting/jobs');
                }
              }}
            />
          )}

          {/* Interviews Tab */}
          {activeTab === 'interviews' && (
            <div className="space-y-6 animate-fadeIn p-2 lg:p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Scheduled Interviews</h2>
                  <p className="text-slate-500 text-sm mt-1">Manage and track your upcoming candidate interviews</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                      {interviews.length} Total Interviews
                    </span>
                  </div>
                </div>
              </div>

              {interviewsLoading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                  <div className="relative">
                    <div className="h-12 w-12 rounded-full border-4 border-cyan-100 border-t-cyan-600 animate-spin"></div>
                    <Calendar className="h-5 w-5 text-cyan-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <p className="mt-4 text-slate-500 font-medium">Loading your schedule...</p>
                </div>
              ) : interviews.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                  <div className="h-16 w-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
                    <Calendar className="h-8 w-8 text-slate-300" />
                  </div>
                  <h3 className="text-slate-900 font-bold">No interviews scheduled</h3>
                  <p className="text-slate-500 text-sm mt-1 max-w-xs text-center">
                    When you schedule interviews with candidates, they will appear here.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {interviews.map((interview) => (
                    <div key={interview.id} className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-cyan-200 transition-all duration-300 p-5 overflow-hidden relative">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-50/30 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:bg-cyan-100/50 transition-colors" />

                      <div className="flex items-start justify-between relative z-10">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            {interview.candidate_photo ? (
                              <img
                                src={getPhotoUrl(interview.candidate_photo)}
                                alt={interview.candidate_name}
                                className="h-14 w-14 rounded-2xl object-cover border-2 border-white shadow-sm"
                              />
                            ) : (
                              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center border-2 border-white shadow-sm text-white font-bold text-xl">
                                {interview.candidate_name.charAt(0)}
                              </div>
                            )}
                            <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-white rounded-full flex items-center justify-center shadow-sm">
                              <CheckCircle className="h-3 w-3 text-emerald-500" />
                            </div>
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900 group-hover:text-cyan-600 transition-colors">{interview.candidate_name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 rounded-lg text-[10px] font-bold text-slate-500 uppercase">
                                <Calendar className="h-3 w-3" />
                                {new Date(interview.date).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-cyan-50 rounded-lg text-[10px] font-bold text-cyan-600 uppercase">
                                <Clock className="h-3 w-3" />
                                {interview.time}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-bold uppercase tracking-wider border border-emerald-100">
                          {interview.status}
                        </div>
                      </div>

                      <div className="mt-5 p-4 bg-slate-50 rounded-xl space-y-3 border border-slate-100 group-hover:bg-white group-hover:border-cyan-100 transition-all">
                        <div className="flex items-start gap-3">
                          <Globe className="h-4 w-4 text-cyan-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-bold text-slate-800 uppercase tracking-tight mb-1">Meeting Link</p>
                            <a
                              href={interview.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-cyan-600 hover:underline break-all font-medium inline-flex items-center gap-1.5"
                            >
                              {interview.meeting_link || 'No link provided'}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                        {interview.notes && (
                          <div className="flex items-start gap-3 pt-2 border-t border-slate-200/50">
                            <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-bold text-slate-400 uppercase tracking-tight mb-1">Preparation Notes</p>
                              <p className="text-sm text-slate-600 line-clamp-2">{interview.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex items-center justify-between gap-3">
                        <button className="flex-1 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-slate-200">
                          Reschedule
                        </button>
                        <button className="flex-1 py-2.5 bg-white text-rose-600 border border-rose-100 rounded-xl text-xs font-bold hover:bg-rose-50 transition-all">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All Candidates Tab */}
          {activeTab === 'all-candidates' && (
            <>
              {/* Show Candidate Profile Page View OR Candidate List */}
              {viewingCandidateProfile && selectedCandidate ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Back Button Header */}
                  <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <button
                      onClick={handleBackToList}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back to Candidates</span>
                    </button>
                    <div className="h-5 w-px bg-gray-200" />
                    <h2 className="text-lg font-bold text-gray-900">{selectedCandidate.name}</h2>
                  </div>
                  {/* Candidate Profile Content */}
                  <div className="p-5">
                    <CandidateProfileView
                      candidate={selectedCandidate}
                      userId={selectedCandidate.id}
                      onClose={handleBackToList}
                      isPageView={true}
                      embedded={true}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* Sub-tabs */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
                    <div className="flex border-b border-gray-100">
                      <button
                        onClick={() => setCandidateSubTab('new')}
                        className={`flex-1 py-3 text-sm font-medium transition-all ${candidateSubTab === 'new'
                          ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50/50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          New Candidates
                          <span className={`text-xs px-2 py-0.5 rounded-full ${candidateSubTab === 'new' ? 'bg-cyan-100 text-cyan-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {sortedCandidates.filter(c => !c.review_status || c.review_status === 'new').length}
                          </span>
                        </span>
                      </button>
                      <button
                        onClick={() => setCandidateSubTab('under-review')}
                        className={`flex-1 py-3 text-sm font-medium transition-all ${candidateSubTab === 'under-review'
                          ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50/50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          Under Review
                          <span className={`text-xs px-2 py-0.5 rounded-full ${candidateSubTab === 'under-review' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {sortedCandidates.filter(c => c.review_status === 'under-review').length}
                          </span>
                        </span>
                      </button>
                      <button
                        onClick={() => setCandidateSubTab('rejected')}
                        className={`flex-1 py-3 text-sm font-medium transition-all ${candidateSubTab === 'rejected'
                          ? 'text-red-600 border-b-2 border-red-600 bg-red-50/50'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          Rejected
                          <span className={`text-xs px-2 py-0.5 rounded-full ${candidateSubTab === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {sortedCandidates.filter(c => c.review_status === 'rejected').length}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Candidate Tiles */}
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
                    </div>
                  ) : sortedCandidates.length === 0 ? (
                    <div className="text-center py-10 bg-white rounded-lg shadow-sm border border-gray-200">
                      <Users className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600">No candidates found matching your filters</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {sortedCandidates
                        .filter(candidate => {
                          if (candidateSubTab === 'new') return !candidate.review_status || candidate.review_status === 'new';
                          if (candidateSubTab === 'under-review') return candidate.review_status === 'under-review';
                          if (candidateSubTab === 'rejected') return candidate.review_status === 'rejected';
                          return true;
                        })
                        .map((candidate) => (
                          <div
                            key={candidate.id}
                            className="bg-white rounded-lg shadow-sm hover:shadow transition-all p-4 cursor-pointer border border-gray-100 hover:border-cyan-200 group"
                            onClick={() => handleViewDetails(candidate)}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className="relative flex-shrink-0">
                                {candidate.photo_url ? (
                                  <img
                                    src={getPhotoUrl(candidate.photo_url)}
                                    alt={candidate.name}
                                    className="h-11 w-11 rounded-lg object-cover border border-white shadow-sm"
                                    style={{ display: 'block' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                    onLoad={(e) => {
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'none';
                                      e.target.style.display = 'block';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="h-11 w-11 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center border border-white shadow-sm"
                                  style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                                >
                                  <span className="text-white font-bold text-base">
                                    {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                                  </span>
                                </div>
                                {candidate.profile_status === 'complete' && (
                                  <div className="absolute -top-0.5 -right-0.5 bg-cyan-500 rounded-full p-0.5 border border-white">
                                    <CheckCircle className="h-2.5 w-2.5 text-white" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-gray-900 truncate group-hover:text-cyan-600 transition-colors">
                                  {candidate.username || candidate.name}
                                </h3>
                                {candidate.occupation && (
                                  <p className="text-xs text-cyan-600 font-medium truncate flex items-center mt-1">
                                    <Briefcase className="h-3 w-3 mr-1 flex-shrink-0" />
                                    <span>{candidate.occupation}</span>
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mb-3">
                              {candidate.german_level && (
                                <div className="bg-cyan-50 rounded-md px-2.5 py-1.5 border border-cyan-100">
                                  <p className="text-[9px] text-cyan-600 font-bold uppercase">German Level</p>
                                  <p className="text-sm font-bold text-cyan-900">{candidate.german_level}</p>
                                </div>
                              )}
                              <div className="bg-slate-50 rounded-md px-2.5 py-1.5 border border-slate-100">
                                <p className="text-[9px] text-slate-500 font-bold uppercase">Completion</p>
                                <p className="text-sm font-bold text-slate-900">{getProfileCompletion(candidate)}%</p>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(candidate);
                                }}
                                className="text-[10px] font-bold text-slate-400 hover:text-cyan-600 transition-colors uppercase tracking-wider"
                              >
                                View Details
                              </button>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowInterest(candidate.id);
                                  }}
                                  className="p-2 rounded-md bg-cyan-50 text-cyan-600 hover:bg-cyan-100 transition-all border border-cyan-100"
                                  title="Send interest"
                                >
                                  <Mail className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCandidateToAddToPool(candidate);
                                    setShowAddSingleCandidateToPoolModal(true);
                                  }}
                                  className="p-2 rounded-md bg-slate-50 text-slate-600 hover:bg-slate-100 transition-all border border-slate-100"
                                  title="Add to pool"
                                >
                                  <FolderPlus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Talent Pool Tab */}
          {activeTab === 'talent-pool' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">{t('dashboard.talentPools')}</h2>
                <button
                  onClick={() => setShowCreatePoolModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-semibold transition-colors shadow-md"
                >
                  <FolderPlus className="h-4 w-4" />
                  <span>Create Pool</span>
                </button>
              </div>

              {/* Manual Pools Section */}
              {manualPools.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Pools</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {manualPools.map(pool => (
                      <div key={pool.id} className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover-lift">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <h3 className="font-semibold text-gray-900 truncate" title={pool.name}>{pool.name}</h3>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleTogglePoolVisibility(pool, pool.is_public)}
                              className={`p-1.5 rounded-lg transition-all ${pool.is_public ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                              title={pool.is_public ? "Public (click to make private)" : "Private (click to make public)"}
                            >
                              {pool.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePool(pool.id);
                              }}
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-all"
                              title="Delete Pool"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleManagePool(pool)}
                              className="text-gray-700 hover:text-gray-900 text-sm font-semibold border-b border-gray-300 hover:border-gray-600 transition-colors whitespace-nowrap"
                            >
                              Manage
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{pool.candidate_count || 0} candidates</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Occupation-based Pools Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Occupation-based Pools</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(() => {
                    // Group candidates by qualification
                    const occupationMap = new Map();
                    sortedCandidates.forEach(candidate => {
                      if (candidate.qualification && candidate.approval_status === 'approved') {
                        const qual = candidate.qualification;
                        if (!occupationMap.has(qual)) {
                          occupationMap.set(qual, []);
                        }
                        occupationMap.get(qual).push(candidate);
                      }
                    });

                    return Array.from(occupationMap.entries()).map(([qualification, candidatesList]) => (
                      <div key={qualification} className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                            <Folder className="h-4 w-4 text-purple-600" />
                            <span>{qualification}</span>
                          </h3>
                          <button
                            onClick={() => {
                              // Show candidates for this occupation
                              setSelectedPool({ id: `occupation-${qualification}`, name: qualification, type: 'occupation' });
                              setPoolCandidates(candidatesList);
                            }}
                            className="text-cyan-600 hover:text-cyan-800 text-sm"
                          >
                            View
                          </button>
                        </div>
                        <p className="text-sm text-gray-600">{candidatesList.length} candidates</p>
                      </div>
                    ));
                  })()}
                </div>
                {(() => {
                  const hasOccupations = sortedCandidates.some(c => c.qualification && c.approval_status === 'approved');
                  if (!hasOccupations) {
                    return (
                      <div className="bg-white rounded-lg shadow-sm p-8 text-center border border-gray-200">
                        <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No occupation-based pools available</p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {/* My Requests Tab */}
          {activeTab === 'my-requests' && (
            <>
              {/* Show Request Details Page View OR Requests List */}
              {viewingRequestDetails && selectedRequest ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Back Button Header */}
                  <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <button
                      onClick={handleBackToRequests}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back to My Requests</span>
                    </button>
                    <div className="h-5 w-px bg-gray-200" />
                    <h2 className="text-lg font-bold text-gray-900">
                      {selectedRequest.title || selectedRequest.requirements || `Request #${selectedRequest.id}`}
                    </h2>
                  </div>

                  {/* Request Details Content */}
                  <div className="p-5">
                    <div className="mb-4">
                      <p className="text-sm text-gray-600">
                        Status: <span className={`font-medium px-2 py-1 rounded ${selectedRequest.status === 'approved' ? 'text-cyan-700 bg-cyan-50 border border-cyan-200' :
                          selectedRequest.status === 'rejected' ? 'text-red-700 bg-red-50 border border-red-200' :
                            'text-yellow-700 bg-yellow-50 border border-yellow-200'
                          }`}>{selectedRequest.status}</span>
                      </p>
                      {selectedRequest.requirements && (
                        <p className="text-sm text-gray-600 mt-2">{selectedRequest.requirements}</p>
                      )}
                    </div>

                    {selectedRequest.granted_candidates && selectedRequest.granted_candidates.length > 0 ? (
                      <>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Granted Candidates ({selectedRequest.granted_candidates.length})
                          </h3>
                          <button
                            onClick={() => handleAddCandidatesToPool(selectedRequest.granted_candidates.map(c => c.id))}
                            className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-semibold transition-colors"
                          >
                            <FolderPlus className="h-4 w-4" />
                            <span>Add All to Pool</span>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedRequest.granted_candidates.map(candidate => (
                            <div
                              key={candidate.id}
                              className="bg-white rounded-lg border border-gray-200 p-4 hover:border-cyan-300 hover:shadow-sm transition-all cursor-pointer"
                              onClick={() => handleViewDetails(candidate)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="relative flex-shrink-0">
                                  {candidate.photo_url ? (
                                    <img
                                      src={getPhotoUrl(candidate.photo_url)}
                                      alt={candidate.name}
                                      className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                                      style={{ display: 'block' }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        const fallback = e.target.nextElementSibling;
                                        if (fallback) fallback.style.display = 'flex';
                                      }}
                                      onLoad={(e) => {
                                        const fallback = e.target.nextElementSibling;
                                        if (fallback) fallback.style.display = 'none';
                                        e.target.style.display = 'block';
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className="h-12 w-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 flex items-center justify-center border-2 border-gray-200"
                                    style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                                  >
                                    <span className="text-white font-semibold text-sm">
                                      {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-gray-900 truncate">{candidate.name}</h4>
                                  {candidate.occupation && (
                                    <p className="text-sm text-gray-600 flex items-center gap-1">
                                      <Briefcase className="h-3 w-3" />
                                      <span>{candidate.occupation}</span>
                                    </p>
                                  )}
                                  {candidate.german_level && (
                                    <p className="text-sm text-gray-600 flex items-center gap-1">
                                      <Award className="h-3 w-3" />
                                      <span>German: {candidate.german_level}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No candidates granted yet</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">My Requests</h2>
                    <button
                      onClick={() => setShowCreateRequestModal(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 border border-cyan-600 font-semibold transition-colors shadow-sm"
                    >
                      <FileText className="h-4 w-4" />
                      <span>Create Request</span>
                    </button>
                  </div>
                  {recruiterRequests.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center border border-gray-200">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No requests found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {recruiterRequests.map(request => (
                        <div
                          key={request.id}
                          className={`bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:border-cyan-500 hover:shadow-lg transition-all ${request.from_feedback ? 'cursor-pointer' : (request.status === 'approved' || (request.granted_candidates && request.granted_candidates.length > 0) ? 'cursor-pointer' : '')
                            }`}
                          onClick={() => {
                            if (request.from_feedback) {
                              handleViewFeedback({ id: request.feedback_id, message: request.requirements, status: request.status, created_at: request.created_at, response_count: request.response_count || 0 });
                            } else if (request.status === 'approved' || (request.granted_candidates && request.granted_candidates.length > 0)) {
                              handleViewRequestCandidates(request);
                            }
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h3 className="font-semibold text-gray-900">{request.title || request.requirements || `Request #${request.id}`}</h3>
                                {request.from_feedback && (
                                  <span className="px-2 py-1 text-xs rounded-full bg-cyan-100 text-cyan-700">
                                    From Feedback
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">Status: <span className={`font-medium px-2 py-1 rounded ${request.status === 'approved' ? 'text-cyan-700 bg-cyan-50 border border-cyan-200' :
                                request.status === 'rejected' ? 'text-red-700 bg-red-50 border border-red-200' :
                                  'text-yellow-700 bg-yellow-50 border border-yellow-200'
                                }`}>{request.status}</span></p>
                              <p className="text-sm text-gray-600">Created: {new Date(request.created_at).toLocaleDateString()}</p>
                              {request.requirements && (
                                <div className="mt-2">
                                  <p className="text-sm font-medium text-gray-900">Requirements:</p>
                                  <p className="text-sm text-gray-600 mt-1">{request.requirements}</p>
                                </div>
                              )}
                              {request.from_feedback && request.response_count > 0 && (
                                <div className="mt-2">
                                  <p className="text-sm text-cyan-600 font-medium">
                                    {request.response_count} response{request.response_count > 1 ? 's' : ''} from Polyglots team - Click to view
                                  </p>
                                </div>
                              )}
                              {request.granted_candidates && request.granted_candidates.length > 0 ? (
                                <div className="mt-3">
                                  <p className="text-sm font-medium text-gray-900">
                                    Granted Candidates ({request.granted_candidates.length}) - Click to view details
                                  </p>
                                  <div className="mt-2 space-y-1">
                                    {request.granted_candidates.slice(0, 3).map(candidate => (
                                      <p key={candidate.id} className="text-sm text-gray-600">
                                        • {candidate.name}
                                        {candidate.occupation && ` (${candidate.occupation})`}
                                      </p>
                                    ))}
                                    {request.granted_candidates.length > 3 && (
                                      <p className="text-sm text-gray-500">... and {request.granted_candidates.length - 3} more</p>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-3">
                                  <p className="text-sm text-gray-500">No candidates granted yet</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* My Feedbacks Tab */}
          {activeTab === 'my-feedbacks' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">My Feedbacks</h2>
              </div>
              {myFeedbacks.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center border border-gray-200">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No feedbacks submitted yet</p>
                  <button
                    onClick={() => document.querySelector('[aria-label="Partner Chatbot"]')?.click()}
                    className="mt-4 text-cyan-600 font-medium hover:underline cursor-pointer"
                  >
                    Submit your first feedback below
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myFeedbacks.map(feedback => (
                    <div
                      key={feedback.id}
                      className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:border-cyan-500 hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => handleViewFeedback(feedback)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900 truncate pr-4 line-clamp-1">
                              {feedback.message && feedback.message.length > 50 ? feedback.message.substring(0, 50) + '...' : feedback.message}
                            </h3>
                            <span className={`text-xs px-2 py-1 rounded-full border ${feedback.priority === 'urgent' ? 'bg-red-50 text-red-700 border-red-200' :
                              feedback.priority === 'high' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                'bg-cyan-50 text-cyan-700 border-cyan-200'
                              }`}>
                              {feedback.priority}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{feedback.message}</p>

                          <div className="flex items-center gap-4 mt-3">
                            <p className="text-xs text-gray-500">
                              Submitted: {new Date(feedback.created_at).toLocaleDateString()}
                            </p>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${feedback.status === 'resolved' ? 'text-cyan-700 bg-cyan-50' :
                              feedback.status === 'responded' ? 'text-cyan-700 bg-cyan-50' :
                                'text-yellow-700 bg-yellow-50'
                              }`}>
                              Status: {feedback.status}
                            </span>
                          </div>

                          {feedback.response_count > 0 && (
                            <div className="mt-3 flex items-center text-sm text-cyan-600 font-medium">
                              <MessageSquare className="h-4 w-4 mr-1" />
                              {feedback.response_count} response{feedback.response_count > 1 ? 's' : ''} from Team
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex-shrink-0 text-gray-400">
                          <ChevronRight className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="border-b border-slate-200 pb-6">
                <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Company Profile</h2>
                <p className="text-slate-500 mt-0.5 uppercase text-[9px] font-bold tracking-widest opacity-70">Your Organization & Contact Details</p>
              </div>

              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden min-h-[600px] relative">
                {fullUser ? (
                  fullUser.error ? (
                    <div className="flex flex-col items-center justify-center py-40">
                      <XCircle className="h-16 w-16 text-red-400 mb-4" />
                      <p className="text-slate-600 font-bold text-sm mb-2">Failed to Load Profile</p>
                      <p className="text-slate-400 text-xs max-w-md text-center">
                        {fullUser.message || 'Unable to fetch profile data. Please try again.'}
                      </p>
                      <button
                        onClick={() => fetchFullUser()}
                        className="mt-6 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all text-sm font-bold"
                      >
                        Retry
                      </button>
                    </div>
                  ) : isEditingProfile ? (
                    <div className="p-6">
                      <div className="mb-6 flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="bg-cyan-100 p-2 rounded-xl">
                            <Edit className="h-5 w-5 text-cyan-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-slate-800">Editing Company Profile</h3>
                            <p className="text-xs text-slate-500">Update your company and contact information</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditingProfile(false)}
                          className="px-4 py-2 text-slate-600 hover:text-slate-900 font-bold text-sm bg-white border border-slate-200 rounded-xl transition-all shadow-sm"
                        >
                          Cancel
                        </button>
                      </div>
                      <ProfileEditor
                        user={fullUser}
                        mode="company"
                        onClose={() => setIsEditingProfile(false)}
                        onSaved={() => {
                          fetchFullUser();
                          setIsEditingProfile(false);
                        }}
                        role="recruiting_partner"
                      />
                    </div>
                  ) : (
                    <div className="p-8">
                      {/* View Only Mode */}
                      <div className="mb-10 flex justify-between items-start">
                        <div className="flex items-center gap-6">
                          <div className="h-24 w-24 rounded-3xl bg-slate-100 flex items-center justify-center border-2 border-slate-200 shadow-sm overflow-hidden">
                            {(fullUser.logo_url || fullUser.photo_url) ? (
                              <img src={getPhotoUrl(fullUser.logo_url || fullUser.photo_url, fullUser.updated_at)} alt={fullUser.legal_entity_name || fullUser.name} className="h-full w-full object-cover" />
                            ) : (
                              <Building2 className="h-10 w-10 text-slate-300" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-3xl font-black text-slate-900 tracking-tight">{fullUser.legal_entity_name || fullUser.name}</h3>
                            <p className="text-slate-500 font-medium flex items-center gap-2 mt-1">
                              <Globe className="h-4 w-4 text-cyan-500" />
                              {fullUser.company_website || 'No website provided'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditingProfile(true)}
                          className="px-6 py-2.5 bg-cyan-600 text-white font-bold rounded-xl hover:bg-cyan-700 shadow-md hover:shadow-lg transition-all flex items-center gap-2 uppercase tracking-widest text-[10px]"
                        >
                          <Edit className="h-4 w-4" />
                          Edit Profile
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-8">
                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                              Company Information
                            </h4>
                            <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Trade Name</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.trade_name || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Founded</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.year_of_establishment || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Company Size</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.company_size || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Specialization</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.specialization || 'N/A'}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Corporate Address</p>
                                <p className="text-sm font-bold text-slate-700 leading-relaxed">{fullUser.address || 'No address provided'}</p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              Point of Contact
                            </h4>
                            <div className="bg-emerald-50/20 rounded-2xl p-6 border border-emerald-100/50 space-y-4">
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Account Manager</p>
                                <p className="text-sm font-bold text-slate-700">{fullUser.account_manager_name || fullUser.name}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Email</p>
                                  <p className="text-sm font-bold text-slate-700 truncate">{fullUser.official_email || fullUser.email}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Phone</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.direct_phone || fullUser.phone || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          {/* Statutory Documents */}
                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                              Statutory Documents
                            </h4>
                            <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 space-y-3">
                              {/* Company Logo */}
                              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                                <div>
                                  <p className="text-xs font-bold text-slate-700">Company Logo</p>
                                  <p className="text-[10px] text-slate-400">Organization brand logo</p>
                                </div>
                                {(fullUser.logo_url || fullUser.photo_url) ? (
                                  <div className="flex items-center gap-2">
                                    <img src={fullUser.logo_url || fullUser.photo_url} alt="Logo" className="h-10 w-10 object-cover rounded-lg border border-slate-200" />
                                    <button onClick={() => handleViewDocument(fullUser.logo_url || fullUser.photo_url)} className="text-[10px] font-bold text-cyan-600 hover:underline">View</button>
                                  </div>
                                ) : (
                                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-lg">Not Uploaded</span>
                                )}
                              </div>
                              {/* Incorporation Certificate */}
                              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                                <div>
                                  <p className="text-xs font-bold text-slate-700">Incorporation Certificate</p>
                                  <p className="text-[10px] text-slate-400">Company registration document</p>
                                </div>
                                {fullUser.incorporation_certificate_url ? (
                                  <button onClick={() => handleViewDocument(fullUser.incorporation_certificate_url)} className="text-[10px] font-bold text-cyan-600 hover:underline bg-cyan-50 px-3 py-1.5 rounded-lg border border-cyan-100">View Doc</button>
                                ) : (
                                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-lg">Not Uploaded</span>
                                )}
                              </div>
                              {/* Labor License */}
                              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                                <div>
                                  <p className="text-xs font-bold text-slate-700">Labor License</p>
                                  <p className="text-[10px] text-slate-400">Labor/recruitment license</p>
                                </div>
                                {fullUser.labor_license_url ? (
                                  <button onClick={() => handleViewDocument(fullUser.labor_license_url)} className="text-[10px] font-bold text-cyan-600 hover:underline bg-cyan-50 px-3 py-1.5 rounded-lg border border-cyan-100">View Doc</button>
                                ) : (
                                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-lg">Not Uploaded</span>
                                )}
                              </div>
                              {/* MSME Certificate */}
                              <div className="flex items-center justify-between py-2">
                                <div>
                                  <p className="text-xs font-bold text-slate-700">MSME / Diversity Certificate</p>
                                  <p className="text-[10px] text-slate-400">Optional compliance document</p>
                                </div>
                                {fullUser.msme_diversity_certificate_url ? (
                                  <button onClick={() => handleViewDocument(fullUser.msme_diversity_certificate_url)} className="text-[10px] font-bold text-cyan-600 hover:underline bg-cyan-50 px-3 py-1.5 rounded-lg border border-cyan-100">View Doc</button>
                                ) : (
                                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-lg">Not Uploaded</span>
                                )}
                              </div>
                              {/* Tax ID */}
                              <div className="pt-2 border-t border-slate-100">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Tax Identification Number</p>
                                <p className="text-sm font-bold text-slate-700">{fullUser.tax_id || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-cyan-600 rounded-3xl p-6 text-white relative overflow-hidden shadow-xl shadow-cyan-500/20">
                            <div className="relative z-10">
                              <h4 className="text-base font-black mb-1">Need to update details?</h4>
                              <p className="text-cyan-100 text-[11px] font-medium leading-relaxed mb-4">Update your company info, contact details and documents here.</p>
                              <button
                                onClick={() => setIsEditingProfile(true)}
                                className="px-5 py-2 bg-white text-cyan-600 font-bold rounded-xl hover:bg-cyan-50 transition-all text-[10px] uppercase tracking-widest shadow-lg"
                              >
                                Start Editing
                              </button>
                            </div>
                            <Building2 className="absolute -bottom-4 -right-4 h-28 w-28 text-cyan-500/20 rotate-12" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center py-40">
                    <div className="h-12 w-12 border-4 border-cyan-500/10 border-t-cyan-600 rounded-full animate-spin mb-4" />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-[9px]">Loading profile...</p>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* My Profile Tab */}
          {activeTab === 'my-profile' && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <PersonalProfileTab user={fullUser} role="recruiting_partner" onSaved={fetchFullUser} />
            </div>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <JobsTab
              jobs={jobs}
              selectedJob={selectedJob}
              setSelectedJob={setSelectedJob}
              jobCandidates={jobCandidates}
              jobRejected={jobRejected}
              showJobForm={showJobForm}
              setShowJobForm={setShowJobForm}
              editingJob={editingJob}
              setEditingJob={setEditingJob}
              fetchJobs={fetchJobs}
              fetchJobCandidates={fetchJobCandidates}
              loading={loading}
              showToast={showToast}
            />
          )}

          {/* Team Members Tab */}
          {activeTab === 'team-members' && (
            <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <TeamMembersTab role="recruiting_partner" />
            </div>
          )}
        </div>
      </div>

      {/* Candidate Details Modal */}
      {showDetailsModal && selectedCandidate && (
        <CandidateProfileView
          candidate={selectedCandidate}
          userId={selectedCandidate.id}
          onClose={() => setShowDetailsModal(false)}
          embedded={true}
        />
      )}

      {/* Pool Management Modal */}
      {selectedPool && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-cyan-500">
            <div className="flex justify-between items-center p-6 border-b-2 border-cyan-500 bg-gradient-to-r from-cyan-500 to-cyan-600">
              <div className="flex items-center space-x-3">
                {viewingCandidateInPool && (
                  <button
                    onClick={() => {
                      setViewingCandidateInPool(false);
                      setSelectedCandidate(null);
                    }}
                    className="text-white hover:text-cyan-100 transition-colors"
                    title="Back to pool"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                )}
                <h2 className="text-xl font-bold text-white">
                  {viewingCandidateInPool && selectedCandidate
                    ? selectedCandidate.name
                    : `${selectedPool.type === 'occupation' ? 'Occupation Pool' : 'Manage Pool'}: ${selectedPool.name}`}
                </h2>
              </div>
              <button
                onClick={() => {
                  setSelectedPool(null);
                  setPoolCandidates([]);
                  setViewingCandidateInPool(false);
                  setSelectedCandidate(null);
                }}
                className="text-white hover:text-cyan-100 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {viewingCandidateInPool && selectedCandidate ? (
                // Show candidate profile inline
                <CandidateProfileView
                  candidate={selectedCandidate}
                  userId={selectedCandidate.id}
                  embedded={true}
                  onClose={() => {
                    setViewingCandidateInPool(false);
                    setSelectedCandidate(null);
                  }}
                />
              ) : (
                // Show pool candidates list
                <>
                  {selectedPool.type === 'occupation' ? (
                    // Occupation pool - read-only view
                    <div className="space-y-3">
                      {poolCandidates.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No candidates in this occupation</p>
                      ) : (
                        poolCandidates.map(candidate => (
                          <div key={candidate.id} className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-cyan-500 hover:border-cyan-600 transition-colors">
                            <div className="flex items-center space-x-3 flex-1">
                              <div className="relative flex-shrink-0">
                                {candidate.photo_url ? (
                                  <img
                                    src={getPhotoUrl(candidate.photo_url)}
                                    alt={candidate.name}
                                    className="h-10 w-10 rounded-full object-cover border-2 border-cyan-500"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 flex items-center justify-center border-2 border-cyan-500"
                                  style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                                >
                                  <span className="text-white font-semibold text-xs">
                                    {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{candidate.name}</p>
                                {candidate.qualification && (
                                  <p className="text-xs text-gray-600 mt-1">{candidate.qualification}</p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleViewDetails(candidate, true)}
                              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-semibold border-2 border-cyan-500 transition-colors"
                            >
                              View Profile
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    // Manual pool - can add/remove candidates
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-black">Candidates in Pool</h3>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={async () => {
                              // Ensure candidates are loaded before showing modal
                              setShowAddCandidateModal(true);
                              if (candidates.length === 0) {
                                setAddCandidateModalLoading(true);
                                try {
                                  const response = await api.get('/recruiting/candidates');
                                  let list = [];
                                  if (Array.isArray(response.data)) list = response.data;
                                  else if (response.data?.candidates) list = response.data.candidates;
                                  setCandidates(list);
                                } catch (e) {
                                  console.error('Error fetching candidates for modal:', e);
                                } finally {
                                  setAddCandidateModalLoading(false);
                                }
                              }
                            }}
                            className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-semibold transition-colors"
                          >
                            <UserPlus className="h-4 w-4" />
                            <span>Add Candidates</span>
                          </button>
                          {poolCandidates.length > 0 && (
                            <button
                              onClick={() => handleShowPoolInterest(selectedPool.id)}
                              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors bg-cyan-600 text-white hover:bg-cyan-700"
                            >
                              <Mail className="h-4 w-4" />
                              <span>Send Pool Interest</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="space-y-3">
                        {poolCandidatesLoading ? (
                          <div className="flex flex-col items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600 mb-3"></div>
                            <p className="text-gray-500 text-sm">Loading pool candidates...</p>
                          </div>
                        ) : (() => {
                          const filteredPoolList = poolCandidates.filter(candidate => {
                            const matchesSearch = searchTerm === '' ||
                              candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (candidate.qualification && candidate.qualification.toLowerCase().includes(searchTerm.toLowerCase())) ||
                              (candidate.experience && candidate.experience.toLowerCase().includes(searchTerm.toLowerCase()));

                            const matchesGermanLevel = filters.germanLevel === 'all' || candidate.german_level === filters.germanLevel;
                            const matchesProfileStatus = filters.profileStatus === 'all' || candidate.profile_status === filters.profileStatus;

                            return matchesSearch && matchesGermanLevel && matchesProfileStatus;
                          });

                          if (poolCandidates.length === 0) {
                            return <p className="text-gray-600 text-center py-8">No candidates in this pool</p>;
                          }
                          if (filteredPoolList.length === 0) {
                            return <p className="text-gray-600 text-center py-8">No candidates match your filters in this pool</p>;
                          }

                          return filteredPoolList.map(candidate => (
                            <div key={candidate.id} className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-cyan-500 hover:border-cyan-600 hover:shadow-lg transition-all">
                              <div className="flex items-center space-x-4 flex-1">
                                {/* Candidate Photo - Always show photo if available, otherwise show placeholder */}
                                <div className="relative flex-shrink-0">
                                  {candidate.photo_url ? (
                                    <img
                                      src={getPhotoUrl(candidate.photo_url)}
                                      alt={candidate.name}
                                      className="h-16 w-16 rounded-full object-cover border-2 border-cyan-500"
                                      style={{ display: 'block' }}
                                      onError={(e) => {
                                        console.error('[Photo Error] Failed to load photo for', candidate.name, ':', getPhotoUrl(candidate.photo_url));
                                        // Hide broken image and show fallback
                                        e.target.style.display = 'none';
                                        const fallback = e.target.nextElementSibling;
                                        if (fallback) {
                                          fallback.style.display = 'flex';
                                        }
                                      }}
                                      onLoad={(e) => {
                                        console.log('[Photo Success] Photo loaded for', candidate.name);
                                        // Ensure fallback is hidden
                                        const fallback = e.target.nextElementSibling;
                                        if (fallback) fallback.style.display = 'none';
                                        // Ensure image is visible
                                        e.target.style.display = 'block';
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className="h-16 w-16 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 flex items-center justify-center border-2 border-cyan-500"
                                    style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                                  >
                                    <span className="text-white font-bold text-lg">
                                      {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900 text-lg">{candidate.name}</p>
                                  <div className="flex flex-wrap gap-3 mt-1">
                                    {candidate.occupation && (
                                      <div className="flex items-center space-x-1">
                                        <Briefcase className="h-4 w-4 text-gray-600" />
                                        <p className="text-sm text-gray-700">{candidate.occupation}</p>
                                      </div>
                                    )}
                                    {candidate.german_level && (
                                      <div className="flex items-center space-x-1">
                                        <Award className="h-4 w-4 text-gray-600" />
                                        <p className="text-sm text-gray-700">German: {candidate.german_level}</p>
                                      </div>
                                    )}
                                    {candidate.qualification && (
                                      <div className="flex items-center space-x-1">
                                        <GraduationCap className="h-4 w-4 text-gray-600" />
                                        <p className="text-sm text-gray-700">{candidate.qualification}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewDetails(candidate, true)}
                                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-semibold transition-colors"
                                >
                                  View Details
                                </button>
                                <button
                                  onClick={() => handleRemoveFromPool(selectedPool.id, candidate.id)}
                                  className="px-3 py-2 bg-white text-black rounded-lg hover:bg-gray-100 text-sm flex items-center space-x-1 font-semibold border-2 border-black transition-colors"
                                >
                                  <UserMinus className="h-4 w-4" />
                                  <span>Remove</span>
                                </button>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Pool Modal */}
      {showCreatePoolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 border-2 border-cyan-500">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Create Talent Pool</h2>
              <button
                onClick={() => {
                  setShowCreatePoolModal(false);
                  setNewPoolName('');
                }}
                className="text-black hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-black mb-2">
                Pool Name
              </label>
              <input
                type="text"
                value={newPoolName}
                onChange={(e) => setNewPoolName(e.target.value)}
                className="w-full px-4 py-2 border-2 border-cyan-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white text-gray-900"
                placeholder="Enter pool name"
              />
            </div>
            <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                id="recruiterPoolPublicToggle"
                checked={newPoolIsPublic}
                onChange={(e) => setNewPoolIsPublic(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              <label htmlFor="recruiterPoolPublicToggle" className="cursor-pointer flex flex-col items-start leading-none">
                <span className="block text-sm font-bold text-gray-800 mb-1">Make Pool Public</span>
                <span className="block text-xs text-gray-500">Share this pool with Polyglots Staff</span>
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreatePoolModal(false);
                  setNewPoolName('');
                }}
                className="px-4 py-2 border-2 border-cyan-500 rounded-lg text-cyan-600 hover:bg-cyan-50 font-semibold bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePool}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-semibold border-2 border-cyan-500 transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Request Modal */}
      {showCreateRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 border-2 border-cyan-500">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Create Candidate Request</h2>
              <button
                onClick={() => {
                  setShowCreateRequestModal(false);
                  setNewRequestTitle('');
                  setNewRequestRequirements('');
                }}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Request Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newRequestTitle}
                  onChange={(e) => setNewRequestTitle(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-cyan-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white text-gray-900 placeholder-gray-400"
                  placeholder="e.g., Need 5 Nurses for Hospital Project"
                  maxLength={100}
                />
                <p className="text-xs text-gray-500 mt-1">A brief title describing your candidate needs</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Detailed Requirements <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newRequestRequirements}
                  onChange={(e) => setNewRequestRequirements(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-cyan-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white text-gray-900 placeholder-gray-400 resize-y"
                  placeholder="Describe the candidate requirements in detail...&#10;&#10;Example:&#10;- Occupation: Nurse&#10;- German Level: B2 or higher&#10;- Experience: Minimum 2 years&#10;- Qualifications: Bachelor's degree in Nursing&#10;- Additional requirements: Valid nursing license"
                  rows={6}
                />
                <p className="text-xs text-gray-500 mt-1">Provide detailed information about the candidates you need</p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateRequestModal(false);
                  setNewRequestTitle('');
                  setNewRequestRequirements('');
                }}
                className="px-6 py-2 border-2 border-cyan-500 rounded-lg text-cyan-600 hover:bg-cyan-50 font-semibold bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRequest}
                className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-semibold border-2 border-cyan-500 transition-colors"
              >
                Create Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Candidates to Pool Modal */}
      {showAddCandidateModal && selectedPool && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 border-2 border-black max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Add Candidates to {selectedPool.name}</h2>
              <button
                onClick={() => {
                  setShowAddCandidateModal(false);
                }}
                className="text-black hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto mb-4">
              {addCandidateModalLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600 mb-3"></div>
                  <p className="text-gray-500 text-sm">Loading candidates...</p>
                </div>
              ) : candidates.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No candidates available</p>
              ) : (
                <div className="space-y-3">
                  {candidates
                    .map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-black transition-colors">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="relative flex-shrink-0">
                            {candidate.photo_url ? (
                              <img
                                src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                alt={candidate.name}
                                className="h-10 w-10 rounded-full object-cover border-2 border-black"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center border-2 border-black"
                              style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                            >
                              <span className="text-white font-semibold text-xs">
                                {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-black">{candidate.name}</p>
                            {candidate.qualification && (
                              <p className="text-sm text-gray-600">{candidate.qualification}</p>
                            )}
                            {candidate.occupation && (
                              <p className="text-sm text-gray-600">{candidate.occupation}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await handleAddToPool(selectedPool.id, candidate.id);
                              setShowAddCandidateModal(false);
                            } catch (error) {
                              console.error('Error adding candidate:', error);
                            }
                          }}
                          className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-semibold border-2 border-black"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowAddCandidateModal(false);
                }}
                className="px-4 py-2 border-2 border-black rounded-lg text-black hover:bg-gray-100 font-semibold bg-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interest Confirmation Modal */}
      {showInterestConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 border-2 border-black animate-slideUp">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Interest Sent</h2>
              <button
                onClick={() => {
                  setShowInterestConfirmationModal(false);
                }}
                className="text-black hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-cyan-100 flex items-center justify-center">
                  <Mail className="h-8 w-8 text-cyan-600" />
                </div>
              </div>
              <p className="text-center text-gray-700 text-lg">
                Polyglots team will contact you to provide more details.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowInterestConfirmationModal(false);
                }}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-semibold border-2 border-black transition-all"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Candidates Modal */}
      {showRequestCandidatesModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-cyan-500 animate-slideUp">
            <div className="sticky top-0 bg-white border-b-2 border-cyan-500 p-6 z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedRequest.title || selectedRequest.requirements || `Request #${selectedRequest.id}`}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Status: <span className={`font-medium px-2 py-1 rounded ${selectedRequest.status === 'approved' ? 'text-cyan-700 bg-cyan-50 border border-cyan-200' :
                      selectedRequest.status === 'rejected' ? 'text-red-700 bg-red-50 border border-red-200' :
                        'text-yellow-700 bg-yellow-50 border border-yellow-200'
                      }`}>{selectedRequest.status}</span>
                  </p>
                  {selectedRequest.requirements && (
                    <p className="text-sm text-gray-600 mt-1">{selectedRequest.requirements}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowRequestCandidatesModal(false);
                    setSelectedRequest(null);
                  }}
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedRequest.granted_candidates && selectedRequest.granted_candidates.length > 0 ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Granted Candidates ({selectedRequest.granted_candidates.length})
                    </h3>
                    <button
                      onClick={() => handleAddCandidatesToPool(selectedRequest.granted_candidates.map(c => c.id))}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-semibold border-2 border-cyan-500 transition-colors"
                    >
                      <FolderPlus className="h-4 w-4" />
                      <span>Add All to Pool</span>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedRequest.granted_candidates.map(candidate => (
                      <div key={candidate.id} className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:border-cyan-500 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="relative flex-shrink-0">
                              {candidate.photo_url ? (
                                <img
                                  src={getPhotoUrl(candidate.photo_url, candidate.updated_at)}
                                  alt={candidate.name}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                                  style={{ display: 'block' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                  onLoad={(e) => {
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) fallback.style.display = 'none';
                                    e.target.style.display = 'block';
                                  }}
                                />
                              ) : null}
                              <div
                                className="h-12 w-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 flex items-center justify-center border-2 border-gray-200"
                                style={{ display: candidate.photo_url ? 'none' : 'flex' }}
                              >
                                <span className="text-white font-semibold text-sm">
                                  {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 truncate">{candidate.name}</h4>
                              {candidate.occupation && (
                                <p className="text-sm text-gray-600 flex items-center space-x-1">
                                  <Briefcase className="h-3 w-3" />
                                  <span>{candidate.occupation}</span>
                                </p>
                              )}
                              {candidate.german_level && (
                                <p className="text-sm text-gray-600 flex items-center space-x-1">
                                  <Award className="h-3 w-3" />
                                  <span>German: {candidate.german_level}</span>
                                </p>
                              )}
                              {candidate.qualification && (
                                <p className="text-sm text-gray-600 flex items-center space-x-1">
                                  <GraduationCap className="h-3 w-3" />
                                  <span className="truncate">{candidate.qualification}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddCandidatesToPool([candidate.id]);
                            }}
                            className="ml-2 flex items-center space-x-1 px-3 py-1 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-xs font-semibold transition-colors"
                            title="Add to Pool"
                          >
                            <FolderPlus className="h-3 w-3" />
                            <span>Add</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No candidates granted for this request yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add to Pool Modal */}
      {showAddToPoolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 border-2 border-cyan-500 animate-slideUp">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add Candidates to Pool</h2>
              <button
                onClick={() => {
                  setShowAddToPoolModal(false);
                  setCandidatesToAddToPool([]);
                }}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Select a pool to add {candidatesToAddToPool.length} candidate(s) to, or create a new pool.
              </p>

              {/* Create New Pool Option */}
              <div className="mb-4 p-4 border-2 border-dashed border-cyan-300 rounded-lg bg-cyan-50">
                <h3 className="font-semibold text-gray-900 mb-2">Create New Pool</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    id="newPoolName"
                    placeholder="Enter pool name"
                    className="flex-1 px-3 py-2 border-2 border-cyan-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                  <button
                    onClick={async () => {
                      const poolNameInput = document.getElementById('newPoolName');
                      const poolName = poolNameInput?.value?.trim();
                      if (!poolName) {
                        showToast('Please enter a pool name');
                        return;
                      }
                      await handleCreatePoolFromRequest(poolName);
                    }}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-semibold border-2 border-cyan-500 transition-colors"
                  >
                    Create & Add
                  </button>
                </div>
              </div>

              {/* Existing Pools */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add to Existing Pool</h3>
                {manualPools.length === 0 ? (
                  <p className="text-gray-500 text-sm">No existing pools. Create a new pool above.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {manualPools.map(pool => (
                      <button
                        key={pool.id}
                        onClick={async () => {
                          await handleAddSelectedCandidatesToPool(pool.id);
                        }}
                        className="w-full text-left p-3 bg-white border-2 border-gray-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">{pool.name}</span>
                          <span className="text-sm text-gray-600">{pool.candidate_count || 0} candidates</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowAddToPoolModal(false);
                  setCandidatesToAddToPool([]);
                }}
                className="px-4 py-2 border-2 border-cyan-500 rounded-lg text-cyan-600 hover:bg-cyan-50 font-semibold bg-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Single Candidate to Pool Modal */}
      {showAddSingleCandidateToPoolModal && candidateToAddToPool && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 border-2 border-black animate-slideUp">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Add Candidate to Pool</h2>
              <button
                onClick={() => {
                  setShowAddSingleCandidateToPoolModal(false);
                  setCandidateToAddToPool(null);
                }}
                className="text-black hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="relative flex-shrink-0">
                  {candidateToAddToPool.photo_url ? (
                    <img
                      src={getPhotoUrl(candidateToAddToPool.photo_url, candidateToAddToPool.updated_at)}
                      alt={candidateToAddToPool.name}
                      className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                      style={{ display: 'block' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'none';
                        e.target.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <div
                    className="h-12 w-12 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center border-2 border-gray-200"
                    style={{ display: candidateToAddToPool.photo_url ? 'none' : 'flex' }}
                  >
                    <span className="text-white font-semibold text-sm">
                      {candidateToAddToPool.name?.charAt(0)?.toUpperCase() || 'C'}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{candidateToAddToPool.name}</p>
                  {candidateToAddToPool.occupation && (
                    <p className="text-sm text-gray-600">{candidateToAddToPool.occupation}</p>
                  )}
                </div>
              </div>

              {/* Create New Pool Option */}
              <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Create New Pool</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    id="newPoolNameSingle"
                    placeholder="Enter pool name"
                    className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black"
                  />
                  <button
                    onClick={async () => {
                      const poolNameInput = document.getElementById('newPoolNameSingle');
                      const poolName = poolNameInput?.value?.trim();
                      if (!poolName) {
                        showToast('Please enter a pool name');
                        return;
                      }
                      try {
                        // Create new pool
                        const poolResponse = await api.post('/recruiting/pools', { name: poolName });
                        const newPoolId = poolResponse.data.pool?.id || poolResponse.data.id;

                        // Add candidate to the new pool
                        await api.post(`/recruiting/pools/${newPoolId}/candidates`, {
                          candidate_ids: [candidateToAddToPool.id]
                        });

                        showToast(`Successfully created pool "${poolName}" and added candidate`);
                        setShowAddSingleCandidateToPoolModal(false);
                        setCandidateToAddToPool(null);
                        // Refresh pools
                        await fetchManualPools();
                      } catch (error) {
                        console.error('Error creating pool:', error);
                        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create pool';
                        showToast(`Failed to create pool: ${errorMessage}`);
                      }
                    }}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-semibold border-2 border-black"
                  >
                    Create & Add
                  </button>
                </div>
              </div>

              {/* Existing Pools */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add to Existing Pool</h3>
                {manualPools.length === 0 ? (
                  <p className="text-gray-500 text-sm">No existing pools. Create a new pool above.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {manualPools.map(pool => (
                      <button
                        key={pool.id}
                        onClick={async () => {
                          try {
                            await api.post(`/recruiting/pools/${pool.id}/candidates`, {
                              candidate_ids: [candidateToAddToPool.id]
                            });
                            showToast(`Successfully added candidate to pool "${pool.name}"`);
                            setShowAddSingleCandidateToPoolModal(false);
                            setCandidateToAddToPool(null);
                            // Refresh pools
                            await fetchManualPools();
                          } catch (error) {
                            console.error('Error adding candidate to pool:', error);
                            const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to add candidate to pool';
                            showToast(`Failed to add candidate to pool: ${errorMessage}`);
                          }
                        }}
                        className="w-full text-left p-3 bg-white border-2 border-gray-200 rounded-lg hover:border-black hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">{pool.name}</span>
                          <span className="text-sm text-gray-600">{pool.candidate_count || 0} candidates</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowAddSingleCandidateToPoolModal(false);
                  setCandidateToAddToPool(null);
                }}
                className="px-4 py-2 border-2 border-black rounded-lg text-black hover:bg-gray-100 font-semibold bg-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 border-2 border-black animate-slideUp">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-black">Reset Password</h2>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
                className="text-black hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handlePasswordReset}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-black mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white text-black pr-10 transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-black mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white text-black pr-10 transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              {passwordError && (
                <div className="mb-4 p-3 bg-black border-2 border-black rounded-lg text-white text-sm">
                  {passwordError}
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                  className="px-4 py-2 border-2 border-black rounded-lg text-black hover:bg-gray-100 font-semibold bg-white transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-semibold border-2 border-black transition-all"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 border-2 border-cyan-500 animate-slideUp flex flex-col max-h-[80vh]">
            <div className="flex justify-between items-center mb-4 border-b pb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Feedback Details</h2>
                <p className="text-sm text-gray-600">
                  Submitted on {new Date(selectedFeedback.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setSelectedFeedback(null);
                  setFeedbackResponses([]);
                }}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-8 pr-2 custom-scrollbar p-6">
              <div className="space-y-6">
                <h4 className="flex items-center space-x-2 text-lg font-bold text-gray-900">
                  <MessageSquare className="h-5 w-5 text-emerald-500" />
                  <span>Conversation Thread</span>
                </h4>

                <div className="space-y-6 mb-8">
                  {/* Original Message as first bubble */}
                  <div className="flex space-x-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm shadow-sm border border-emerald-200">
                      Me
                    </div>
                    <div className="flex-1">
                      <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl rounded-tl-none p-5 shadow-sm">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-bold text-emerald-900">You (Original)</span>
                          <span className="text-xs text-gray-400">{new Date(selectedFeedback.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedFeedback.message || selectedFeedback.requirements}</p>
                      </div>
                    </div>
                  </div>

                  {feedbackResponses.map((response) => (
                    <div key={response.id} className="flex space-x-4">
                      <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md ${response.responder_role === 'recruiting_partner' ? 'bg-emerald-600' : 'bg-gray-800'
                        }`}>
                        {response.responder_name?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className={`bg-white border rounded-2xl rounded-tl-none p-5 shadow-sm ${response.responder_role === 'recruiting_partner' ? 'border-emerald-100' : 'border-gray-100'
                          }`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-bold text-gray-900">{response.responder_name}</span>
                            <span className="text-xs text-gray-400">{new Date(response.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{response.response_text}</p>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 ml-2 capitalize">{response.responder_role.replace('_', ' ')}</p>
                      </div>
                    </div>
                  ))}

                  {feedbackResponses.length === 0 && (
                    <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-2xl">
                      <p className="text-gray-400">Waiting for response from Polyglots Team...</p>
                    </div>
                  )}
                </div>

                {selectedFeedback.status !== 'resolved' ? (
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <h5 className="text-sm font-bold text-gray-700">Continue Conversation</h5>
                      <button
                        onClick={handleMarkFeedbackResolved}
                        className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center space-x-1 transition-colors"
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>Mark as Resolved</span>
                      </button>
                    </div>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Type your reply here..."
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[100px] resize-none text-sm transition-all shadow-sm"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleSendResponse}
                        disabled={!responseText.trim() || isSendingResponse}
                        className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md flex items-center space-x-2"
                      >
                        {isSendingResponse ? (
                          <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        <span>Send Reply</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-center">
                    <div className="inline-flex items-center justify-center p-2 bg-emerald-100 rounded-full text-emerald-600 mb-3">
                      <CheckCircle className="h-6 w-6" />
                    </div>
                    <p className="font-bold text-emerald-800">This conversation has been resolved.</p>
                    <p className="text-sm text-emerald-600 mt-1">Thank you for your feedback!</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 pt-4 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setSelectedFeedback(null);
                  setFeedbackResponses([]);
                }}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("RecruitingPartnerDashboard Error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 bg-red-50 text-red-900 border border-red-200 rounded-lg m-4">
          <h2 className="text-xl font-bold mb-4">Something went wrong in the Recruiting Dashboard</h2>
          <details className="whitespace-pre-wrap font-mono text-sm bg-white p-4 rounded border border-red-100 overflow-auto">
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrap the component with ErrorBoundary for export
const RecruitingPartnerDashboardWithBoundary = () => (
  <ErrorBoundary>
    <RecruitingPartnerDashboard />
  </ErrorBoundary>
);

export default RecruitingPartnerDashboardWithBoundary;
